import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../../components/layout/Layout';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import { BookOpen, PlusCircle, Edit, Trash2, ChevronDown, ChevronRight, Plus, Search, X, FolderTree, ArrowLeft, FolderInput, Trash, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import ReorderTopicsModal from '../../components/admin/ReorderTopicsModal';
import { useNavigate } from 'react-router-dom';
import { TopicPicker as TopicPickerTree } from '../../components/shared/TopicPicker';
import { MAX_TOPIC_DEPTH } from '../../config/topicConfig';

// Collect every descendant id of `rootId` (excluding the root itself)
// by walking the flat `topics` array. Cycle-safe via `visited`. Used
// by the delete-preview + count computation so the dialog reflects
// the full subtree, not just direct children.
const collectDescendants = (rootId, topicsList) => {
  const out = new Set();
  const byParent = new Map();
  topicsList.forEach((t) => {
    const p = t.parent_topic_id || null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(t);
  });
  const frontier = [rootId];
  let hops = 0;
  while (frontier.length && hops < 20) {
    const next = [];
    for (const pid of frontier) {
      for (const child of (byParent.get(pid) || [])) {
        if (!out.has(child.id)) {
          out.add(child.id);
          next.push(child.id);
        }
      }
    }
    frontier.length = 0;
    frontier.push(...next);
    hops += 1;
  }
  return out;
};

// SubtreePreview — visual read-only tree render for the delete dialog.
// mode='cascade'  → root + every descendant is styled red-line-through
// mode='promote'  → root is red, direct children get an arrow chip
//                   showing where they will move to (grandparent name
//                   or "become root"), grandchildren+ stay neutral
//                   because they're untouched.
const SubtreePreview = ({ rootTopic, topics: allTopics, mode }) => {
  if (!rootTopic) return null;
  const byParent = new Map();
  allTopics.forEach((t) => {
    const p = t.parent_topic_id || null;
    if (!byParent.has(p)) byParent.set(p, []);
    byParent.get(p).push(t);
  });
  const grandparent = rootTopic.parent_topic_id
    ? allTopics.find((t) => t.id === rootTopic.parent_topic_id)
    : null;
  const promoteTargetLabel = grandparent ? grandparent.name : 'root level';

  const renderNode = (node, depth, isRootSubtree) => {
    const children = byParent.get(node.id) || [];
    const isRoot = node.id === rootTopic.id;
    const isDeleted = mode === 'cascade' && isRootSubtree;
    const isPromoteRoot = mode === 'promote' && isRoot;
    const isPromoteChild = mode === 'promote' && !isRoot && node.parent_topic_id === rootTopic.id;
    return (
      <div key={node.id}>
        <div
          className="flex items-start gap-1.5 py-0.5"
          style={{ paddingLeft: `${depth * 14}px` }}
        >
          {depth > 0 && <span className="text-gray-300 flex-shrink-0 select-none">└</span>}
          <span
            className={
              isDeleted
                ? 'text-red-700 line-through decoration-red-500/70 min-w-0 break-words'
                : isPromoteRoot
                  ? 'text-red-700 line-through decoration-red-500/70 min-w-0 break-words'
                  : 'text-gray-700 min-w-0 break-words'
            }
          >
            {node.name}
          </span>
          {isPromoteChild && (
            <span className="ml-1 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-px whitespace-nowrap flex-shrink-0">
              → moves under {promoteTargetLabel}
            </span>
          )}
        </div>
        {children.map((child) => renderNode(child, depth + 1, isRootSubtree))}
      </div>
    );
  };

  return (
    <div className="mt-3 p-3 rounded-md border border-gray-200 bg-gray-50 max-h-52 overflow-y-auto text-sm">
      {renderNode(rootTopic, 0, true)}
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const grades = ['MEO Class 1', 'MEO Class 2', 'MEO Class 4', 'ETO', 'Master', 'Chief Mate', '2MFG'];

/**
 * TopicTreeNode — recursive tree render (iter 105).
 *
 * Renders one node + its children (which recursively render *their*
 * children). Enforces MAX_TOPIC_DEPTH by hiding the "Add sub-topic"
 * button once the current depth is at the cap. Long topic names wrap
 * gracefully via `break-words` + `line-clamp-2` so no label ever
 * overflows the container regardless of depth or name length.
 *
 * Depth is 0-indexed (root = 0).
 */
const TopicTreeNode = ({
  topic,
  depth,
  topics,
  grade,
  funcId,
  openAddDialog,
  openEditDialog,
  handleDelete,
  expandedTopics,
  toggleTopic,
  openReorder,
}) => {
  const children = topics.filter((t) => t.parent_topic_id === topic.id);
  const canAddSubtopic = depth < MAX_TOPIC_DEPTH - 1;
  const isLeaf = children.length === 0;
  const isExpanded = expandedTopics.has(topic.id);
  const canReorderChildren = children.length >= 2;

  return (
    <div>
      <div
        className={`flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-gray-50 group ${
          depth > 0 ? '' : ''
        }`}
      >
        <div className="flex items-start gap-2 min-w-0 flex-1">
          {isLeaf ? (
            <span className="mt-1.5 w-2 h-px bg-gray-300 flex-shrink-0" />
          ) : (
            <button
              type="button"
              onClick={() => toggleTopic(topic.id)}
              className="mt-0.5 w-4 h-4 flex items-center justify-center rounded hover:bg-gray-200 flex-shrink-0"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              data-testid={`toggle-topic-${topic.id}`}
            >
              <ChevronRight
                className={`w-3 h-3 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              />
            </button>
          )}
          <span
            className={`min-w-0 break-words line-clamp-2 leading-snug ${
              depth === 0 ? 'font-medium text-gray-800' : 'text-gray-600'
            } ${!isLeaf ? 'cursor-pointer' : ''}`}
            onClick={() => { if (!isLeaf) toggleTopic(topic.id); }}
            title={topic.name}
          >
            {topic.name}
          </span>
          {children.length > 0 && (
            <Badge variant="outline" className="text-xs h-5 px-1.5 flex-shrink-0">
              {children.length} sub
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
          {canAddSubtopic && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openAddDialog(grade, funcId, topic.id)}
              className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
              title="Add sub-topic"
              data-testid={`add-subtopic-${topic.id}`}
            >
              <Plus className="w-4 h-4" />
            </Button>
          )}
          {canReorderChildren && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openReorder(funcId, topic.id, `${topic.name} · Children`)}
              className="h-7 w-7 p-0 text-slate-600 hover:bg-slate-100"
              title="Reorder children"
              data-testid={`reorder-children-${topic.id}`}
            >
              <ArrowUpDown className="w-4 h-4" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => openEditDialog(topic)}
            className="h-7 w-7 p-0 text-[#6366F1] hover:bg-[#6366F1]/10"
            title="Edit topic"
            data-testid={`edit-topic-${topic.id}`}
          >
            <Edit className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDelete(topic.id, topic.name)}
            className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
            title="Delete topic"
            data-testid={`delete-topic-${topic.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {children.length > 0 && isExpanded && (
        <div className="ml-5 pl-3 border-l-2 border-gray-200 space-y-1">
          {children.map((child) => (
            <TopicTreeNode
              key={child.id}
              topic={child}
              depth={depth + 1}
              topics={topics}
              grade={grade}
              funcId={funcId}
              openAddDialog={openAddDialog}
              openEditDialog={openEditDialog}
              handleDelete={handleDelete}
              expandedTopics={expandedTopics}
              toggleTopic={toggleTopic}
              openReorder={openReorder}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const AdminTopicsPage = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    grade: '',
    function_id: '',
    parent_topic_id: null
  });
  
  // Expansion states - grades and functions collapsed by default
  const [expandedGrades, setExpandedGrades] = useState({});
  const [expandedFunctions, setExpandedFunctions] = useState({});
  // Per-topic expansion. Collapsed by default — critical when a function has
  // 100+ topics with sub-topics (previously EVERY descendant rendered
  // unconditionally, making the tree unreadable). Set of topic IDs.
  const [expandedTopics, setExpandedTopics] = useState(() => new Set());

  // Reorder modal state — single component reused at every depth level.
  // `scope` carries what siblings to load into the modal. `null` = closed.
  const [reorderScope, setReorderScope] = useState(null);

  const openReorder = (functionId, parentTopicId, scopeLabel) => {
    setReorderScope({ functionId, parentTopicId, scopeLabel });
  };

  const closeReorder = () => setReorderScope(null);

  // Derive siblings for the currently-open reorder scope, respecting
  // current sort_order (backend already returns topics sorted).
  const reorderSiblings = reorderScope
    ? topics
        .filter((t) =>
          t.function_id === reorderScope.functionId &&
          (t.parent_topic_id || null) === (reorderScope.parentTopicId || null)
        )
        .map((t) => ({ id: t.id, name: t.name }))
    : [];

  const toggleTopic = (topicId) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      if (next.has(topicId)) next.delete(topicId); else next.add(topicId);
      return next;
    });
  };

  // Expand every topic in the function whose subtree contains at least one
  // node. Used by the "Expand all" chip and by the searchbox so results
  // aren't hidden inside collapsed parents.
  const expandAllInFunction = (functionId) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      topics.forEach((t) => {
        if (t.function_id === functionId) next.add(t.id);
      });
      return next;
    });
  };

  const collapseAllInFunction = (functionId) => {
    setExpandedTopics((prev) => {
      const next = new Set(prev);
      topics.forEach((t) => {
        if (t.function_id === functionId) next.delete(t.id);
      });
      return next;
    });
  };
  
  // Search states - per function
  const [functionSearches, setFunctionSearches] = useState({});
  
  // Global search
  const [globalSearch, setGlobalSearch] = useState('');

  // Parent-with-children delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, name, childCount }
  // When backend returns 409 (notes exist) we surface a proper dialog
  // that lets admin REASSIGN the notes to another topic instead of
  // forcing a destructive delete. Shape:
  //   { notesCount, deletedIds, sourceName, retry: () => Promise }
  const [notesBlock, setNotesBlock] = useState(null);
  const [reassignTargetId, setReassignTargetId] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchFunctions();
    fetchTopics();
  }, []);

  const fetchFunctions = async () => {
    try {
      // Only fetch ORAL functions - written functions inherit topics from linked oral ones
      const response = await axios.get(`${API}/functions?exam_type=orals`);
      setFunctions(response.data);
    } catch (error) {
      console.error('Failed to fetch functions:', error);
    }
  };

  const fetchTopics = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/topics`);
      setTopics(response.data);
    } catch (error) {
      console.error('Failed to fetch topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/topics`, formData);
      toast.success('Topic created successfully');
      setDialogOpen(false);
      resetForm();
      fetchTopics();
    } catch (error) {
      console.error('Failed to create topic:', error);
      toast.error('Failed to create topic');
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/topics/${currentTopic.id}`, formData);
      toast.success('Topic updated successfully');
      setDialogOpen(false);
      setEditMode(false);
      setCurrentTopic(null);
      resetForm();
      fetchTopics();
    } catch (error) {
      console.error('Failed to update topic:', error);
      // Surface the cycle-guard message verbatim so admins know exactly
      // what's wrong (e.g. "Cannot move this topic under one of its own
      // sub-topics — that would create a cycle.").
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update topic');
    }
  };

  const handleDelete = async (id, topicName) => {
    // iter 108e: count the FULL subtree (all descendants at any depth),
    // not just direct children. Deep trees hide impact behind a plain
    // "N sub-topics" count — the confirm dialog needs to reflect what
    // will actually go.
    const descendants = collectDescendants(id, topics);
    const childCount = descendants.size;
    // Single confirmation surface for both paths: leaf (childCount=0) and
    // parent (childCount>0). The AlertDialog branches its copy + actions
    // on childCount. This replaces the older window.confirm() leaf flow
    // for UX consistency.
    setDeleteTarget({ id, name: topicName, childCount });
  };

  // Used by the parent-with-children AlertDialog. Also used for leaf
  // deletes (childCount=0) where cascade is irrelevant — the backend
  // simply deletes the single topic.
  const executeLeafDelete = async () => {
    if (!deleteTarget) return;
    const { id, name } = deleteTarget;
    setDeleting(true);
    try {
      const resp = await axios.delete(`${API}/topics/${id}`);
      const updated = (resp?.data?.questions_updated || 0) + (resp?.data?.written_questions_updated || 0);
      toast.success(
        updated > 0
          ? `Topic deleted. Removed reference from ${updated} question${updated !== 1 ? 's' : ''}.`
          : 'Topic deleted successfully'
      );
      setDeleteTarget(null);
      fetchTopics();
    } catch (error) {
      // 409 — topic has notes. Surface reassign dialog (same logic as
      // before, just lifted out of the window.confirm path).
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === 'topic_has_notes') {
        setDeleteTarget(null);
        setNotesBlock({
          notesCount: detail.notes_count,
          deletedIds: detail.deleted_ids || [id],
          sourceName: name,
          retry: async (forceDelete) => {
            const resp2 = await axios.delete(
              `${API}/topics/${id}${forceDelete ? '?force_delete_notes=true' : ''}`
            );
            const notesDel = resp2?.data?.notes_deleted || 0;
            const refsCleaned = (resp2?.data?.questions_updated || 0) + (resp2?.data?.written_questions_updated || 0);
            const bits = ['Topic deleted'];
            if (notesDel > 0) bits.push(`removed ${notesDel} note${notesDel !== 1 ? 's' : ''}`);
            if (refsCleaned > 0) bits.push(`cleaned ${refsCleaned} reference${refsCleaned !== 1 ? 's' : ''}`);
            toast.success(bits.join(' • '));
            fetchTopics();
          },
        });
        return;
      }
      console.error('Failed to delete topic:', error);
      toast.error('Failed to delete topic');
    } finally {
      setDeleting(false);
    }
  };


  // Used by the parent-with-children AlertDialog
  const executeParentDelete = async (cascade, forceDeleteNotes = false) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const params = new URLSearchParams();
      params.set('cascade', cascade ? 'true' : 'false');
      if (forceDeleteNotes) params.set('force_delete_notes', 'true');
      const resp = await axios.delete(`${API}/topics/${deleteTarget.id}?${params}`);
      const {
        deleted = 0,
        promoted = 0,
        questions_updated = 0,
        written_questions_updated = 0,
        notes_deleted = 0,
      } = resp?.data || {};
      const refsCleaned = questions_updated + written_questions_updated;
      const bits = [];
      if (cascade) {
        bits.push(`Deleted ${deleted} topic${deleted !== 1 ? 's' : ''}`);
      } else {
        bits.push('Topic deleted');
        if (promoted > 0) bits.push(`promoted ${promoted} subtopic${promoted !== 1 ? 's' : ''} to top-level`);
      }
      if (refsCleaned > 0) bits.push(`cleaned ${refsCleaned} question reference${refsCleaned !== 1 ? 's' : ''}`);
      if (notes_deleted > 0) bits.push(`removed ${notes_deleted} note${notes_deleted !== 1 ? 's' : ''}`);
      toast.success(bits.join(' • '));
      setDeleteTarget(null);
      fetchTopics();
    } catch (error) {
      // 409 — open reassign dialog with retry that uses the cascade flag.
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409 && detail?.code === 'topic_has_notes') {
        const targetId = deleteTarget.id;
        const targetName = deleteTarget.name;
        setDeleteTarget(null);
        setNotesBlock({
          notesCount: detail.notes_count,
          deletedIds: detail.deleted_ids || [targetId],
          sourceName: targetName,
          retry: async (forceDelete) => {
            const params2 = new URLSearchParams();
            params2.set('cascade', cascade ? 'true' : 'false');
            if (forceDelete) params2.set('force_delete_notes', 'true');
            const resp2 = await axios.delete(`${API}/topics/${targetId}?${params2}`);
            const notesDel = resp2?.data?.notes_deleted || 0;
            toast.success(
              `Topic deleted${notesDel > 0 ? ` • removed ${notesDel} note${notesDel !== 1 ? 's' : ''}` : ''}`
            );
            fetchTopics();
          },
        });
        return;
      }
      console.error('Failed to delete topic:', error);
      toast.error('Failed to delete topic');
    } finally {
      setDeleting(false);
    }
  };

  // Reassign all notes from notesBlock.deletedIds → target topic, then
  // retry the topic delete. Closes dialog on success.
  const reassignAndDelete = async () => {
    if (!notesBlock || !reassignTargetId) return;
    setReassigning(true);
    try {
      const { data } = await axios.post(`${API}/notes/bulk-move-topic`, {
        from_topic_ids: notesBlock.deletedIds,
        to_topic_id: reassignTargetId,
      });
      toast.success(`Moved ${data.moved} note${data.moved !== 1 ? 's' : ''} to new topic`);
      // Now the source topics have no notes — retry the delete cleanly.
      await notesBlock.retry(false);
      setNotesBlock(null);
      setReassignTargetId('');
    } catch (e) {
      console.error('Reassign failed:', e);
      toast.error('Failed to reassign notes');
    } finally {
      setReassigning(false);
    }
  };

  const confirmDeleteWithNotes = async () => {
    if (!notesBlock) return;
    setReassigning(true);
    try {
      await notesBlock.retry(true);
      setNotesBlock(null);
      setReassignTargetId('');
    } catch (e) {
      console.error('Force delete failed:', e);
      toast.error('Failed to delete topic with notes');
    } finally {
      setReassigning(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', grade: '', function_id: '', parent_topic_id: null });
  };

  // Open dialog to add topic to a specific function
  const openAddDialog = (grade, functionId, parentTopicId = null) => {
    setEditMode(false);
    setCurrentTopic(null);
    setFormData({
      name: '',
      description: '',
      grade: grade,
      function_id: functionId,
      parent_topic_id: parentTopicId
    });
    setDialogOpen(true);
  };

  // Open dialog to edit a topic
  const openEditDialog = (topic) => {
    setEditMode(true);
    setCurrentTopic(topic);
    setFormData({
      name: topic.name,
      description: topic.description || '',
      grade: topic.grade,
      function_id: topic.function_id,
      parent_topic_id: topic.parent_topic_id || null
    });
    setDialogOpen(true);
  };

  // Toggle grade expansion
  const toggleGrade = (grade) => {
    setExpandedGrades(prev => ({ ...prev, [grade]: !prev[grade] }));
  };

  // Toggle function expansion
  const toggleFunction = (functionId) => {
    setExpandedFunctions(prev => ({ ...prev, [functionId]: !prev[functionId] }));
  };

  // Update search for a specific function. When the admin types anything,
  // auto-expand every topic in this function so matches nested deep in the
  // tree aren't hidden inside collapsed parents. Clearing the search
  // leaves expansion untouched — admin's manual toggles survive.
  const updateFunctionSearch = (functionId, value) => {
    setFunctionSearches(prev => ({ ...prev, [functionId]: value }));
    if (value && value.trim()) {
      expandAllInFunction(functionId);
    }
  };

  // Get functions for a grade
  const getFunctionsForGrade = (grade) => {
    return functions.filter(f => f.grade === grade);
  };

  // Get topics for a function (with search filter)
  const getTopicsForFunction = (functionId) => {
    const searchTerm = functionSearches[functionId]?.toLowerCase() || '';
    return topics.filter(t => 
      t.function_id === functionId &&
      (searchTerm === '' || t.name.toLowerCase().includes(searchTerm))
    );
  };

  // Get parent topics for a function
  const getParentTopicsForFunction = (functionId) => {
    const funcTopics = getTopicsForFunction(functionId);
    return funcTopics.filter(t => !t.parent_topic_id);
  };

  // Get child topics for a parent
  const getChildTopics = (parentId) => {
    return topics.filter(t => t.parent_topic_id === parentId);
  };

  // Get function display name
  const getFunctionDisplay = (func) => {
    const suffix = func.function_suffix && func.function_suffix !== 'null' && func.function_suffix !== 'None' ? func.function_suffix : '';
    return `F${func.function_number}${suffix}`;
  };

  // Get available parent topics for the form.
  // iter 108b: return a filtered flat topics list — <TopicPickerTree>
  // builds the tree itself, gives us search + scroll + breadcrumb.
  // Excludes:
  //   • self (the topic being edited)
  //   • all descendants of self (cycle prevention — mirrors backend)
  //   • any topic whose depth would put a new child past the "+" icon
  //     rule (i.e. depth >= MAX_TOPIC_DEPTH − 1). This is intentionally
  //     one level stricter than the raw backend guard so the picker
  //     agrees with the tree-row "+" icon (canAddSubtopic). Keeping
  //     both entry points on the same rule is what the user requested.
  // Filtering the flat array is safe: we only ever drop leaves (deepest
  // nodes) or the edited subtree — parents of the surviving nodes are
  // always kept, so no orphans are created for the tree render.
  const getAvailableParentTopics = () => {
    const funcTopics = topics.filter(
      (t) => t.grade === formData.grade && t.function_id === formData.function_id
    );
    if (funcTopics.length === 0) return [];

    // Build parent → children lookup once.
    const byParent = new Map();
    funcTopics.forEach((t) => {
      const p = t.parent_topic_id || null;
      if (!byParent.has(p)) byParent.set(p, []);
      byParent.get(p).push(t);
    });

    // Compute depth for every topic (0-indexed).
    const depthOf = new Map();
    const walkDepth = (node, depth) => {
      depthOf.set(node.id, depth);
      (byParent.get(node.id) || []).forEach((c) => walkDepth(c, depth + 1));
    };
    (byParent.get(null) || []).forEach((r) => walkDepth(r, 0));
    // Orphans: assign depth 0.
    funcTopics.forEach((t) => {
      if (!depthOf.has(t.id)) depthOf.set(t.id, 0);
    });

    // Collect self + descendants when editing.
    const excludedIds = new Set();
    if (editMode && currentTopic?.id) {
      excludedIds.add(currentTopic.id);
      const frontier = [currentTopic.id];
      while (frontier.length) {
        const next = [];
        for (const pid of frontier) {
          for (const child of funcTopics) {
            if (child.parent_topic_id === pid && !excludedIds.has(child.id)) {
              excludedIds.add(child.id);
              next.push(child.id);
            }
          }
        }
        frontier.length = 0;
        frontier.push(...next);
      }
    }

    // Match the tree-row "+" icon rule (canAddSubtopic, line ~148):
    // a topic at `depth` can only be picked as a parent when
    //   depth < MAX_TOPIC_DEPTH - 1
    // With MAX_TOPIC_DEPTH = 5 that caps children at 4 levels below
    // root (Root, L1, L2, L3, L4) — the "+" is hidden on L4 topics
    // like "Piston Rod 5", so the picker must hide them too or the
    // two entry points would disagree.
    return funcTopics.filter(
      (t) => !excludedIds.has(t.id) && (depthOf.get(t.id) ?? 0) < MAX_TOPIC_DEPTH - 1
    );
  };

  // Global search results
  const globalSearchResults = globalSearch.trim() 
    ? topics.filter(t => t.name.toLowerCase().includes(globalSearch.toLowerCase()))
    : [];

  // Count topics for a grade
  const getTopicCountForGrade = (grade) => {
    return topics.filter(t => t.grade === grade).length;
  };

  // Count topics for a function
  const getTopicCountForFunction = (functionId) => {
    return topics.filter(t => t.function_id === functionId).length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center py-12">
          <div className="spinner"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Reorder modal — single instance for the whole page, driven by
          `reorderScope` state. Reusable at every tree depth. */}
      <ReorderTopicsModal
        open={!!reorderScope}
        onClose={closeReorder}
        functionId={reorderScope?.functionId}
        parentTopicId={reorderScope?.parentTopicId ?? null}
        siblings={reorderSiblings}
        scopeLabel={reorderScope?.scopeLabel || ''}
        onSaved={() => { fetchTopics(); }}
      />
      <div className="page-transition">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/content')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Content Management
        </Button>
        
        {/* Header */}
        <Card className="mb-6">
          <CardHeader className="bg-gradient-to-r from-[#6366F1]/5 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-[#6366F1] rounded-lg">
                  <FolderTree className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-2xl">Topic Management</CardTitle>
                  <CardDescription>
                    Organize questions by topics within oral functions. Written functions inherit topics from their linked oral function.
                  </CardDescription>
                </div>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold text-[#6366F1]">{topics.length}</p>
                <p className="text-sm text-gray-500">Total Topics</p>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Global Search */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                placeholder="Search topics across all functions..."
                className="pl-10 pr-10"
              />
              {globalSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setGlobalSearch('')}
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Global Search Results */}
            {globalSearchResults.length > 0 && (
              <div className="mt-3 border rounded-lg max-h-60 overflow-y-auto">
                <div className="p-2 bg-gray-50 border-b text-sm font-medium text-gray-600">
                  Found {globalSearchResults.length} topic{globalSearchResults.length !== 1 ? 's' : ''}
                </div>
                {globalSearchResults.map(topic => {
                  const func = functions.find(f => f.id === topic.function_id);
                  const parent = topic.parent_topic_id ? topics.find(t => t.id === topic.parent_topic_id) : null;
                  return (
                    <div 
                      key={topic.id} 
                      className="p-3 border-b last:border-b-0 hover:bg-gray-50 flex items-center justify-between"
                    >
                      <div>
                        <p className="font-medium">{topic.name}</p>
                        <p className="text-xs text-gray-500">
                          {topic.grade} → {func ? `${getFunctionDisplay(func)} - ${func.name}` : 'Unknown'}
                          {parent && ` → ${parent.name}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(topic)}
                          className="h-7 w-7 p-0 text-[#6366F1]"
                          data-testid={`topic-edit-${topic.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(topic.id, topic.name)}
                          className="h-7 w-7 p-0 text-red-600"
                          data-testid={`topic-delete-${topic.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Topic Tree by Grade & Function */}
        <Card>
          <CardContent className="p-6">
            <div className="space-y-3">
              {grades.map((grade) => {
                const gradeFunctions = getFunctionsForGrade(grade);
                const topicCount = getTopicCountForGrade(grade);
                
                if (gradeFunctions.length === 0) return null;
                
                return (
                  <div key={grade} className="border rounded-lg overflow-hidden">
                    {/* Grade Header */}
                    <Collapsible open={expandedGrades[grade]} onOpenChange={() => toggleGrade(grade)}>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          className="w-full justify-between p-4 h-auto font-semibold text-left hover:bg-gray-50 rounded-none"
                        >
                          <span className="flex items-center gap-3">
                            {expandedGrades[grade] ? (
                              <ChevronDown className="w-5 h-5 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-5 h-5 text-gray-500" />
                            )}
                            <Badge className="bg-[#6366F1]">{grade}</Badge>
                            <span className="text-sm font-normal text-gray-500">
                              {gradeFunctions.length} function{gradeFunctions.length !== 1 ? 's' : ''} • {topicCount} topic{topicCount !== 1 ? 's' : ''}
                            </span>
                          </span>
                        </Button>
                      </CollapsibleTrigger>
                      
                      <CollapsibleContent>
                        <div className="border-t bg-gray-50/50">
                          {gradeFunctions.map((func) => {
                            const funcTopicCount = getTopicCountForFunction(func.id);
                            const parentTopics = getParentTopicsForFunction(func.id);
                            const searchTerm = functionSearches[func.id] || '';
                            
                            return (
                              <div key={func.id} className="border-b last:border-b-0">
                                {/* Function Header */}
                                <Collapsible 
                                  open={expandedFunctions[func.id]} 
                                  onOpenChange={() => toggleFunction(func.id)}
                                >
                                  <div className="flex items-center">
                                    <CollapsibleTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        className="flex-1 justify-start p-3 pl-8 h-auto font-medium text-left hover:bg-white rounded-none"
                                      >
                                        <span className="flex items-center gap-2">
                                          {expandedFunctions[func.id] ? (
                                            <ChevronDown className="w-4 h-4 text-[#6366F1]" />
                                          ) : (
                                            <ChevronRight className="w-4 h-4 text-[#6366F1]" />
                                          )}
                                          <BookOpen className="w-4 h-4 text-[#6366F1]" />
                                          <span className="text-[#6366F1]">{getFunctionDisplay(func)}</span>
                                          <span className="text-gray-700">- {func.name}</span>
                                          <Badge variant="secondary" className="ml-2 text-xs">
                                            {funcTopicCount}
                                          </Badge>
                                        </span>
                                      </Button>
                                    </CollapsibleTrigger>
                                    
                                    {/* Reorder parent topics — only shown when there's ≥2 to shuffle */}
                                    {parentTopics.length >= 2 && (
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openReorder(func.id, null, `${func.function_number} · ${func.name} · Parent Topics`);
                                        }}
                                        className="mr-2 h-8 px-2 text-slate-700 hover:bg-slate-100"
                                        title="Reorder parent topics in this function"
                                        data-testid={`reorder-parents-${func.id}`}
                                      >
                                        <ArrowUpDown className="w-4 h-4 mr-1" />
                                        <span className="text-xs">Reorder</span>
                                      </Button>
                                    )}

                                    {/* Add topic button */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openAddDialog(grade, func.id, null);
                                      }}
                                      className="mr-3 h-8 px-2 text-[#6366F1] hover:bg-[#6366F1]/10"
                                      title="Add parent topic"
                                    >
                                      <PlusCircle className="w-4 h-4 mr-1" />
                                      <span className="text-xs">Add Topic</span>
                                    </Button>
                                  </div>
                                  
                                  <CollapsibleContent>
                                    <div className="pl-12 pr-4 pb-3 bg-white">
                                      {/* Search within function */}
                                      {funcTopicCount > 3 && (
                                        <div className="relative mb-3">
                                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-gray-400" />
                                          <Input
                                            value={searchTerm}
                                            onChange={(e) => updateFunctionSearch(func.id, e.target.value)}
                                            placeholder="Search topics in this function..."
                                            className="pl-7 h-8 text-sm"
                                          />
                                          {searchTerm && (
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                                              onClick={() => updateFunctionSearch(func.id, '')}
                                            >
                                              <X className="w-3 h-3" />
                                            </Button>
                                          )}
                                        </div>
                                      )}
                                      
                                      {/* Topics Tree (iter 105: recursive render — any depth up to MAX_TOPIC_DEPTH) */}
                                      {parentTopics.length > 0 ? (
                                        <>
                                          <div className="flex items-center justify-end gap-2 mb-2 text-[11px]">
                                            <button
                                              type="button"
                                              onClick={() => expandAllInFunction(func.id)}
                                              className="text-[#6366F1] hover:underline"
                                              data-testid={`expand-all-${func.id}`}
                                            >
                                              Expand all
                                            </button>
                                            <span className="text-gray-300">·</span>
                                            <button
                                              type="button"
                                              onClick={() => collapseAllInFunction(func.id)}
                                              className="text-gray-600 hover:underline"
                                              data-testid={`collapse-all-${func.id}`}
                                            >
                                              Collapse all
                                            </button>
                                          </div>
                                          <div className="space-y-1">
                                            {parentTopics.map((parentTopic) => (
                                              <TopicTreeNode
                                                key={parentTopic.id}
                                                topic={parentTopic}
                                                depth={0}
                                                topics={topics}
                                                grade={grade}
                                                funcId={func.id}
                                                openAddDialog={openAddDialog}
                                                openEditDialog={openEditDialog}
                                                handleDelete={handleDelete}
                                                expandedTopics={expandedTopics}
                                                toggleTopic={toggleTopic}
                                                openReorder={openReorder}
                                              />
                                            ))}
                                          </div>
                                        </>
                                      ) : (
                                        <div className="text-center py-4 text-sm text-gray-500 italic">
                                          No topics yet. Click &quot;Add Topic&quot; to create one.
                                        </div>
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              </div>
                            );
                          })}
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Add/Edit Topic Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editMode ? 'Edit Topic' : (formData.parent_topic_id ? 'Add Subtopic' : 'Add Topic')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={editMode ? handleUpdate : handleCreate} className="space-y-4 mt-4">
              {/* Context display */}
              <div className="p-3 bg-[#6366F1]/5 border border-[#6366F1]/20 rounded-lg">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <Badge className="bg-[#6366F1]">{formData.grade}</Badge>
                  <span className="text-gray-400">→</span>
                  <Badge variant="outline" className="border-[#6366F1] text-[#6366F1]">
                    {functions.find(f => f.id === formData.function_id)?.name || 'Select function'}
                  </Badge>
                  {formData.parent_topic_id && (
                    <>
                      <span className="text-gray-400">→</span>
                      <Badge variant="outline" className="border-purple-500 text-purple-600">
                        {topics.find(t => t.id === formData.parent_topic_id)?.name || 'Parent'}
                      </Badge>
                    </>
                  )}
                </div>
              </div>

              {/* Only show grade/function selects if not pre-filled */}
              {!formData.grade && (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Grade *</Label>
                    <Select
                      value={formData.grade}
                      onValueChange={(value) => setFormData({ ...formData, grade: value, function_id: '', parent_topic_id: null })}
                      required
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select grade" />
                      </SelectTrigger>
                      <SelectContent>
                        {grades.map((grade) => (
                          <SelectItem key={grade} value={grade}>{grade}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label>Oral Function *</Label>
                    <Select
                      value={formData.function_id}
                      onValueChange={(value) => setFormData({ ...formData, function_id: value, parent_topic_id: null })}
                      required
                      disabled={!formData.grade}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select function" />
                      </SelectTrigger>
                      <SelectContent>
                        {functions.filter(f => f.grade === formData.grade).map((func) => (
                          <SelectItem key={func.id} value={func.id}>
                            {getFunctionDisplay(func)} - {func.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* Parent topic selector (for creating subtopics) */}
              {formData.function_id && (() => {
                // Reparent-in-edit gotcha: because the tree view only renders
                // two levels (top-level + direct children), moving a topic
                // that itself has children would create hidden grandchildren.
                // Block that shape by disabling the picker with a helpful
                // hint. The API also accepts this — it's the UI contract we
                // are protecting, not the DB.
                const isEditingParentWithChildren =
                  editMode &&
                  currentTopic &&
                  topics.some(t => t.parent_topic_id === currentTopic.id);
                // In create mode the picker only makes sense before a
                // parent is chosen (matches original behaviour). In edit
                // mode we always show it so admins can reparent or promote
                // to root.
                const shouldShow = editMode || !formData.parent_topic_id;
                if (!shouldShow) return null;
                return (
                  <div>
                    <Label>Parent Topic (Optional)</Label>
                    <div className="mt-1" data-testid="topic-parent-select">
                      <TopicPickerTree
                        topics={getAvailableParentTopics()}
                        value={formData.parent_topic_id}
                        onChange={(value) =>
                          setFormData({ ...formData, parent_topic_id: value || null })
                        }
                        placeholder="None (Root Topic)"
                        allowClear
                        disabled={isEditingParentWithChildren}
                        testIdPrefix="topic-parent"
                      />
                    </div>
                    {isEditingParentWithChildren && (
                      <p className="text-xs text-amber-700 mt-1">
                        This topic has {topics.filter(t => t.parent_topic_id === currentTopic.id).length} subtopic
                        {topics.filter(t => t.parent_topic_id === currentTopic.id).length === 1 ? '' : 's'}.
                        Move or delete the subtopics first, then reparent this topic.
                      </p>
                    )}
                  </div>
                );
              })()}

              <div>
                <Label>Topic Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Diesel Engine Systems"
                  required
                  className="mt-1"
                  autoFocus
                />
              </div>
              
              <div>
                <Label>Description (Optional)</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the topic"
                  rows={3}
                  className="mt-1"
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-[#6366F1] hover:bg-[#6366F1]/90">
                  {editMode ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Topic delete confirmation — branches on whether the topic
            has subtopics. Leaf-topic delete (childCount=0) uses a simple
            confirm copy + single destructive action. Parent-with-children
            keeps the promote/cascade choice. Both surfaces share the
            same shadcn AlertDialog for visual consistency. */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && !deleting && setDeleteTarget(null)}>
          <AlertDialogContent
            data-testid={deleteTarget?.childCount > 0 ? "parent-topic-delete-dialog" : "leaf-topic-delete-dialog"}
            className={deleteTarget?.childCount > 0 ? "max-w-3xl" : ""}
          >
            {deleteTarget?.childCount > 0 ? (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete &quot;{deleteTarget?.name}&quot;?</AlertDialogTitle>
                  <AlertDialogDescription asChild>
                    <div>
                      <p>
                        This topic has <span className="font-semibold">{deleteTarget?.childCount} sub-topic{deleteTarget?.childCount !== 1 ? 's' : ''} across its subtree</span>.
                        Choose what to do with them:
                      </p>
                      {/* iter 108e: two side-by-side previews so the
                          admin sees exactly what each choice will do to
                          the tree before clicking. Prevents accidental
                          subtree loss on deep trees. */}
                      {deleteTarget && (() => {
                        const rootTopic = topics.find((t) => t.id === deleteTarget.id);
                        if (!rootTopic) return null;
                        return (
                          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <div className="text-xs font-semibold text-gray-900 mb-1">
                                Keep sub-topics
                              </div>
                              <div className="text-xs text-gray-500 mb-1">
                                Only &quot;{rootTopic.name}&quot; is removed. Direct children move up one level.
                              </div>
                              <SubtreePreview
                                rootTopic={rootTopic}
                                topics={topics}
                                mode="promote"
                              />
                            </div>
                            <div>
                              <div className="text-xs font-semibold text-red-700 mb-1">
                                Delete everything
                              </div>
                              <div className="text-xs text-gray-500 mb-1">
                                Root + all {deleteTarget.childCount} descendant{deleteTarget.childCount !== 1 ? 's' : ''} are permanently removed.
                              </div>
                              <SubtreePreview
                                rootTopic={rootTopic}
                                topics={topics}
                                mode="cascade"
                              />
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                  <AlertDialogCancel
                    disabled={deleting}
                    data-testid="parent-topic-delete-cancel"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); executeParentDelete(false); }}
                    disabled={deleting}
                    className="bg-[#0A7EA4] hover:bg-[#086283]"
                    data-testid="parent-topic-delete-promote"
                  >
                    Keep subtopics
                  </AlertDialogAction>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); executeParentDelete(true); }}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="parent-topic-delete-cascade"
                  >
                    Delete all {deleteTarget ? deleteTarget.childCount + 1 : ''} topics
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            ) : (
              <>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete &quot;{deleteTarget?.name}&quot;?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will permanently delete the topic. Any question references
                    pointing to it will be cleaned up automatically. If this topic
                    still has notes attached, you&apos;ll be prompted to reassign them.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel
                    disabled={deleting}
                    data-testid="leaf-topic-delete-cancel"
                  >
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={(e) => { e.preventDefault(); executeLeafDelete(); }}
                    disabled={deleting}
                    className="bg-red-600 hover:bg-red-700"
                    data-testid="leaf-topic-delete-confirm"
                  >
                    {deleting ? 'Deleting...' : 'Delete topic'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </>
            )}
          </AlertDialogContent>
        </AlertDialog>

        {/* Notes-block dialog — when topic delete returns 409, admin can
            either reassign the attached notes to another topic OR opt-in
            to deleting them outright. Default radio is REASSIGN (soft
            path). The destination picker excludes the source ids so a
            no-op move is impossible. */}
        <Dialog open={!!notesBlock} onOpenChange={(open) => { if (!open && !reassigning) { setNotesBlock(null); setReassignTargetId(''); } }}>
          <DialogContent className="max-w-lg" data-testid="topic-delete-notes-block-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderInput className="w-5 h-5 text-amber-600" />
                Found {notesBlock?.notesCount} note{notesBlock?.notesCount !== 1 ? 's' : ''} on &quot;{notesBlock?.sourceName}&quot;
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                You can reassign these notes to another topic so they stay accessible — or delete them along with the topic.
              </p>

              <div className="rounded-md border bg-blue-50/40 p-3 space-y-2">
                <Label className="text-sm font-medium flex items-center gap-2">
                  <FolderInput className="w-4 h-4 text-blue-600" />
                  Reassign all {notesBlock?.notesCount} note{notesBlock?.notesCount !== 1 ? 's' : ''} to:
                </Label>
                {/* iter 108e: swapped the old flat 2-level admin/TopicPicker
                    for the unified shared TopicPickerTree — searchable +
                    scrollable + tree browse with breadcrumb chips, matching
                    every other admin picker post-iter-108c. Source topic
                    subtree is excluded so a no-op move is impossible. */}
                <TopicPickerTree
                  topics={topics.filter(
                    (t) => !(notesBlock?.deletedIds || []).includes(t.id)
                  )}
                  value={reassignTargetId}
                  onChange={(id) => setReassignTargetId(id || '')}
                  placeholder="Search a destination topic…"
                  allowClear
                  testIdPrefix="reassign-pick"
                />
              </div>

              <div className="rounded-md border border-red-200 bg-red-50/40 p-3 text-xs text-red-700">
                <div className="font-medium flex items-center gap-1 mb-1">
                  <Trash className="w-3 h-3" />
                  Alternative: delete the notes too
                </div>
                <p>Take a copy first if you want to keep the content — deletion cannot be undone.</p>
              </div>
            </div>
            <AlertDialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => { setNotesBlock(null); setReassignTargetId(''); }}
                disabled={reassigning}
                data-testid="topic-delete-notes-block-cancel"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={confirmDeleteWithNotes}
                disabled={reassigning}
                data-testid="topic-delete-notes-block-force"
              >
                <Trash className="w-4 h-4 mr-1" /> Delete topic + notes
              </Button>
              <Button
                onClick={reassignAndDelete}
                disabled={reassigning || !reassignTargetId}
                data-testid="topic-delete-notes-block-reassign"
              >
                <FolderInput className="w-4 h-4 mr-1" />
                {reassigning ? 'Reassigning…' : 'Reassign notes + delete topic'}
              </Button>
            </AlertDialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminTopicsPage;
